import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Container,
    Grid,
    Alert,
    CircularProgress
} from '@mui/material';
import { Link as MuiLink } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { CustomTextField, CustomPasswordField } from '../../components/common/CustomTextFields';
import { PrimaryButton } from '../../components/common/Buttons';
import AuthCard from '../../layouts/AuthCard';

const validationSchema = Yup.object({
    email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    password: Yup.string()
        .required('Password is required')
});

const Login = () => {
    const navigate = useNavigate();
    const { login: loginUser } = useAuth();
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                setError(null);
                console.log('🔐 Attempting login...');
                const response = await loginUser(values);
                console.log('✅ Login successful, redirecting...');
                const roleName = response?.user?.role?.name;
                if (roleName === 'Trainer') {
                    navigate('/training');
                } else if (roleName === 'Trainee') {
                    navigate('/training/portal');
                } else {
                    navigate('/');
                }
            } catch (error) {
                console.error('❌ Login error:', error);
                setError(error.message || 'Login failed. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    });

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {/* Logo */}
                <Box
                    component="img"
                    src="/Reflection_logo_with_text.svg"
                    alt="Reflection Logo"
                    sx={{
                        width: 220,
                        height: 'auto',
                        mb: 3,
                    }}
                />
                <AuthCard>
                    {error && (
                        <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                            {error}
                        </Alert>
                    )}
                    <Box
                        component="form"
                        onSubmit={formik.handleSubmit}
                        sx={{ mt: 1, width: '100%' }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <CustomTextField
                                    fullWidth
                                    id="email"
                                    name="email"
                                    label="Email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CustomPasswordField
                                    fullWidth
                                    id="password"
                                    name="password"
                                    label="Password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    error={formik.touched.password && Boolean(formik.errors.password)}
                                    helperText={formik.touched.password && formik.errors.password}
                                />
                            </Grid>
                        </Grid>
                        <PrimaryButton
                            type="submit"
                            fullWidth
                            sx={{ mt: 4, mb: 2 }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Sign in'}
                        </PrimaryButton>
                    </Box>
                </AuthCard>
            </Box>
            <Box sx={{ position: 'fixed', bottom: 20, left: 0, width: '100%', textAlign: 'center', zIndex: 10 }}>
                <MuiLink
                    href="https://enableyou.co/"
                    target="_blank"
                    rel="noopener"
                    underline="none"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: 'text.secondary',
                        fontSize: 13,
                        opacity: 0.7
                    }}
                >
                    <span>Powered by</span>
                    <Box
                        component="img"
                        src="/Enable_with_text.svg"
                        alt="Enable"
                        sx={{
                            height: 28,
                            width: 'auto',
                        }}
                    />
                </MuiLink>
            </Box>
        </Container>
    );
};

export default Login; 